import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ImagingInstance } from "$global-providers/rxdb-provider/collections/imaging-instance";
import {
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
  useRxCollectionImagingInstance,
} from "$global-providers/rxdb-provider/collections/imaging-instance";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { useReplicateImagingInstance } from "$global-providers/rxdb-provider/replication/imaging-instance";
import { useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { GE_PRESET_TO_EXAM_TYPE } from "../../../_lib/dicom-preset-mapping";
import type { HydratedExamImage } from "../../../_lib/hooks/use-epocus-exam-images";
import { useEpocusExamImages } from "../../../_lib/hooks/use-epocus-exam-images";
import { useAutoAssignDicomImages } from "./use-auto-assign-dicom-images";
import { useSyncEncounterDeviceFromScan } from "./use-sync-encounter-device-from-scan";

/** Maps an ImagingInstance to the ExamImage shape expected by useEpocusExamImages. */
function toExamImage(instance: ImagingInstance, index: number) {
  return {
    id: instance.id,
    previewS3Key: instance.previewS3Key,
    viewLabel: `#${index + 1}`,
    dicomPreset: instance.dicomPreset ?? null,
    physicalDeltaMm: instance.physicalDeltaMm ?? null,
  };
}

/**
 * Returns true when the instance has an unambiguous DICOM preset that maps to
 * a specific exam type — meaning it will be auto-assigned and should not appear
 * in the root encounter gallery.
 */
function isAutoAssignable(instance: ImagingInstance): boolean {
  const preset = instance.dicomPreset;
  if (!preset) return false;
  const examType = GE_PRESET_TO_EXAM_TYPE[preset];
  return !!examType; // non-null, non-undefined → unambiguous mapping
}

/**
 * Drives DICOM imaging for an accession-only encounter (no patientId).
 *
 * - Server-side replication: enabled when `dicomStudyId` is non-null; pulls
 *   imaging instances from the API filtered by that study ID.
 * - Local RxDB query: enabled when `accessionNumber` is non-null; may surface
 *   any previously replicated instances for that accession (e.g. from a prior
 *   session) even if `dicomStudyId` is null.
 * - Instances with an unambiguous DICOM preset (TTE, VASCULAR) are auto-assigned
 *   to their matching exam via useAutoAssignDicomImages and excluded from the
 *   returned list.
 * - Instances with ambiguous or unknown presets (CHEST, ABDOMINAL, …) are
 *   returned for display in the root-level UnmatchedDicomSection.
 *
 * `instanceCount` counts every instance found for the encounter, before the
 * auto-assign filter drops any. Callers deciding whether images have arrived
 * at all must use it rather than `images.length`, which goes back to zero as
 * instances are assigned into exams.
 */
export const useEncounterDicomImages = ({
  accessionNumber,
  dicomStudyId,
  encounterId,
  examCollection,
  encounterCollection,
  currentDeviceId,
  userId,
}: {
  accessionNumber: string | null;
  dicomStudyId: string | null;
  encounterId: string | null | undefined;
  examCollection: RxCollection<EpocusExam> | null;
  encounterCollection: RxCollection<EpocusEncounter> | null;
  currentDeviceId: string | null | undefined;
  userId: string;
}): { images: HydratedExamImage[]; instanceCount: number } => {
  const { db } = useRxDBContext();
  const collection = useRxCollectionImagingInstance();

  // Start (or join) a replication keyed on dicomStudyId for this encounter.
  useReplicateImagingInstance({
    db,
    collection: collection ?? undefined,
    dicomStudyId,
  });

  const { result: instances } = useRxData<ImagingInstance>(
    RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
    // When accessionNumber is a real value, query by imagingStudyAccessionNumber.
    // Also include dicomStudyId as an OR clause when available — this handles the
    // window after link-patient where the local cache still has instances stored
    // under dicomStudyId before the next replication cycle updates their
    // imagingStudyAccessionNumber (PNE-443).
    // When accessionNumber is null (GE VScan Air — no accession), query by
    // dicomStudyId only. dicomStudyId is the ImagingStudyExternalDetails PK
    // and uniquely identifies the study, so this is always correctly scoped even
    // when multiple no-accession studies exist simultaneously.
    accessionNumber != null
      ? (col) =>
          col.find({
            selector: {
              $or: [
                { imagingStudyAccessionNumber: { $eq: accessionNumber } },
                ...(dicomStudyId != null
                  ? [{ dicomStudyId: { $eq: dicomStudyId } }]
                  : []),
              ],
            },
            sort: [{ id: "asc" }],
          })
      : dicomStudyId != null
        ? (col) =>
            col.find({
              selector: { dicomStudyId: { $eq: dicomStudyId } },
              sort: [{ id: "asc" }],
            })
        : undefined,
  );

  // Auto-assign instances with unambiguous presets to their matching exam type.
  // PNE-213: creates/updates exam documents in RxDB as a background side-effect.
  useAutoAssignDicomImages({ encounterId, instances, examCollection, userId });

  // PNE-427: scan data is authoritative for the encounter device — patch it
  // silently whenever attached instances reveal a recognised device identity.
  useSyncEncounterDeviceFromScan({
    encounterId,
    instances,
    encounterCollection,
    currentDeviceId,
  });

  // Only hide auto-assignable instances from the root gallery when examCollection
  // is available — meaning auto-assign has had a chance to run. If examCollection
  // is null, auto-assign returns early so we show all instances here rather than
  // letting them disappear from the UI entirely.
  const unmatchedInstances = useMemo(
    () =>
      examCollection
        ? instances.filter((inst) => !isAutoAssignable(inst))
        : [...instances],
    [instances, examCollection],
  );

  const examImages = useMemo(
    () => unmatchedInstances.map((inst, i) => toExamImage(inst, i)),
    [unmatchedInstances],
  );

  const images = useEpocusExamImages(examImages);

  return { images, instanceCount: instances.length };
};
